
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Database } from "@/types/supabase";

type Child = Database["public"]["Tables"]["children"]["Row"];

export function useChildrenData() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);

  useEffect(() => {
    fetchChildren();
  }, [navigate, toast]);

  const fetchChildren = async () => {
    try {
      setIsLoading(true);
      
      // Get current user first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/sign-in");
        return;
      }

      // Get caregiver ID for the current user
      const { data: caregiverData, error: caregiverError } = await supabase
        .from('caregivers')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (caregiverError) {
        console.error("Error fetching caregiver:", caregiverError);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load caregiver information. Please try again later.",
        });
        setIsLoading(false);
        return;
      }

      // Now fetch children for this caregiver
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("caregiver_id", caregiverData.id)
        .eq("isdeleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setChildren(data || []);
    } catch (error) {
      console.error("Error fetching children:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load children. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddChild = () => {
    setAddDialogOpen(true);
  };

  const handleChildAdded = async (childId: string) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get caregiver ID
      const { data: caregiverData } = await supabase
        .from('caregivers')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!caregiverData) return;

      // Get the newly added child
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("id", childId)
        .eq("caregiver_id", caregiverData.id)
        .single();

      if (error) throw error;

      setChildren((prevChildren) => [data, ...prevChildren]);
      toast({
        title: "Success",
        description: "Child added successfully.",
      });
    } catch (error) {
      console.error("Error fetching added child:", error);
    }
  };

  const handleEditChild = (child: Child) => {
    setSelectedChild(child);
    setEditDialogOpen(true);
  };

  const handleChildUpdated = (updatedChild: Child) => {
    setChildren((prevChildren) =>
      prevChildren.map((child) =>
        child.id === updatedChild.id ? updatedChild : child
      )
    );
    setEditDialogOpen(false);
    toast({
      title: "Success",
      description: "Child updated successfully.",
    });
  };

  const handleDeleteChild = (childId: string) => {
    const childToDelete = children.find((child) => child.id === childId);
    if (childToDelete) {
      setSelectedChild(childToDelete);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedChild) return;

    try {
      const { error } = await supabase
        .from("children")
        .update({ isdeleted: true })
        .eq("id", selectedChild.id);

      if (error) throw error;

      setChildren((prevChildren) =>
        prevChildren.filter((child) => child.id !== selectedChild.id)
      );
      setDeleteDialogOpen(false);
      toast({
        title: "Success",
        description: "Child removed successfully.",
      });
    } catch (error) {
      console.error("Error deleting child:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete child. Please try again later.",
      });
    }
  };

  return {
    isLoading,
    children,
    addDialogOpen,
    setAddDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    selectedChild,
    handleAddChild,
    handleChildAdded,
    handleEditChild,
    handleChildUpdated,
    handleDeleteChild,
    handleConfirmDelete
  };
}
